var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import { RDB_TYPE_ID, RSCHEMA_TYPE_ID, RTABLE_GROUP_TYPE_ID } from "@hyper-hyper-space/hhs3_rdb";
export class RootIndex {
    constructor() {
        this.roots = new Map();
    }
    upsert(record) {
        const existing = this.roots.get(record.id);
        this.roots.set(record.id, Object.assign(Object.assign({}, existing), record));
    }
    registerObject(id, object, name) {
        const existing = this.roots.get(id);
        this.upsert({
            id,
            type: object.getType(),
            kind: kindFromType(object.getType()),
            name: name !== null && name !== void 0 ? name : existing === null || existing === void 0 ? void 0 : existing.name,
            object,
        });
    }
    list(kind) {
        const roots = [...this.roots.values()];
        return kind === undefined ? roots : roots.filter((root) => root.kind === kind);
    }
    get(id) {
        return this.roots.get(id);
    }
    resolveSchema(ref_1) {
        return __awaiter(this, arguments, void 0, function* (ref, ctx = {}) {
            const root = yield this.resolveRoot(ref, ['schema'], ctx);
            return { id: root.id, schema: root.object };
        });
    }
    resolveGroup(ref_1) {
        return __awaiter(this, arguments, void 0, function* (ref, ctx = {}) {
            const root = yield this.resolveRoot(ref, ['group'], ctx);
            return { id: root.id, group: root.object };
        });
    }
    resolveDatabase(ref_1) {
        return __awaiter(this, arguments, void 0, function* (ref, ctx = {}) {
            const root = yield this.resolveRoot(ref, ['database'], ctx);
            return { id: root.id, db: root.object };
        });
    }
    resolveTable(ref_1) {
        return __awaiter(this, arguments, void 0, function* (ref, ctx = {}) {
            if (ref.group === undefined)
                throw new Error(`Table '${ref.table}' requires a group qualifier`);
            const group = yield this.resolveGroup(ref.group, ctx);
            if (group.group === undefined)
                throw new Error(`Group '${refText(ref.group)}' is not loaded`);
            const table = yield group.group.getTable(ref.table);
            return { groupId: group.id, group: group.group, tableName: ref.table, table };
        });
    }
    resolveLogTarget(ref_1) {
        return __awaiter(this, arguments, void 0, function* (ref, ctx = {}) {
            if (ref.kind === 'name' && ref.text.includes('.')) {
                const parts = ref.text.split('.');
                const groupRef = { kind: 'name', text: parts[0], parts: [parts[0]], span: ref.span };
                const table = yield this.resolveTable({ group: groupRef, table: parts.slice(1).join('.'), span: ref.span }, ctx);
                return {
                    kind: 'table',
                    id: table.table.getId(),
                    object: table.table,
                    groupId: table.groupId,
                    group: table.group,
                    tableName: table.tableName,
                };
            }
            const root = yield this.resolveRoot(ref, ['database', 'schema', 'group'], ctx);
            if (root.object === undefined)
                throw new Error(`Root '${root.id}' is not loaded`);
            if (root.kind === 'database')
                return { kind: 'database', id: root.id, object: root.object };
            if (root.kind === 'schema')
                return { kind: 'schema', id: root.id, object: root.object };
            return { kind: 'group', id: root.id, object: root.object };
        });
    }
    resolveHash(ref, scope) {
        return __awaiter(this, void 0, void 0, function* () {
            const candidates = yield this.hashCandidates(scope);
            const matches = candidates.filter((hash) => hash.startsWith(ref.prefix));
            if (matches.length === 1)
                return matches[0];
            if (matches.length === 0)
                throw new Error(`Unknown hash prefix '#${ref.prefix}'`);
            throw new Error(`Ambiguous hash prefix '#${ref.prefix}'`);
        });
    }
    resolveRoot(ref, kinds, ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = yield this.resolveRootId(ref, kinds, ctx);
            const root = this.roots.get(id);
            if (root === undefined)
                throw new Error(`Unknown root '${refText(ref)}'`);
            if (!kinds.includes(root.kind)) {
                throw new Error(`Root '${refText(ref)}' is a ${root.kind}, expected ${kinds.join(' or ')}`);
            }
            return root;
        });
    }
    resolveRootId(ref, kinds, ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (ref.kind === 'name') {
                for (const kind of kinds) {
                    const aliasScope = rootKindToAliasScope(kind);
                    const aliased = (_a = ctx.aliases) === null || _a === void 0 ? void 0 : _a.get(aliasScope, ref.text);
                    if (aliased !== undefined)
                        return aliased;
                }
                const matches = [...this.roots.values()].filter((root) => kinds.includes(root.kind) && root.name === ref.text);
                if (matches.length === 1)
                    return matches[0].id;
                if (matches.length > 1)
                    throw new Error(`Ambiguous root name '${ref.text}'`);
                if (this.roots.has(ref.text)) {
                    const root = this.roots.get(ref.text);
                    if (kinds.includes(root.kind))
                        return ref.text;
                }
                throw new Error(`Unknown root '${ref.text}'`);
            }
            return this.resolveHash(ref, { kind: 'global' });
        });
    }
    hashCandidates(scope) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, e_1, _b, _c;
            if (scope.kind === 'global')
                return [...this.roots.keys()];
            const root = this.roots.get(scope.objectId);
            const object = root === null || root === void 0 ? void 0 : root.object;
            if (object === undefined)
                return [...this.roots.keys()];
            const dag = yield object.getScopedDag();
            const hashes = [];
            try {
                for (var _d = true, _e = __asyncValues(dag.loadAllEntries()), _f; _f = yield _e.next(), _a = _f.done, !_a; _d = true) {
                    _c = _f.value;
                    _d = false;
                    const entry = _c;
                    hashes.push(entry.hash);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = _e.return)) yield _b.call(_e);
                }
                finally { if (e_1) throw e_1.error; }
            }
            return hashes;
        });
    }
}
export function kindFromType(type) {
    if (type === RDB_TYPE_ID)
        return 'database';
    if (type === RSCHEMA_TYPE_ID)
        return 'schema';
    if (type === RTABLE_GROUP_TYPE_ID)
        return 'group';
    return 'other';
}
function rootKindToAliasScope(kind) {
    if (kind === 'database')
        return 'db';
    if (kind === 'schema')
        return 'schema';
    if (kind === 'group')
        return 'group';
    throw new Error(`Root kind '${kind}' is not aliasable`);
}
function refText(ref) {
    return ref.kind === 'name' ? ref.text : `#${ref.prefix}`;
}
